import { createStyles, makeStyles } from "@material-ui/core";
import {
    Avatar,
    EmailLink,
    LayoutContainer,
    MessageComposerStandardButton,
    PhoneLink,
    RelodeTheme,
    Text,
} from "@relode/core";
import classnames from "classnames";
import * as React from "react";

import { JobManagerContact } from "../../types";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
            },
            detailsContainer: {
                display: "flex",
                flexDirection: "row",
                flexGrow: 1,
                margin: theme.spacing(3, 0),
            },
            contactLink: {
                marginBottom: theme.spacing(1),
            },
            avatar: {
                height: 56,
                width: 56,
                marginRight: theme.spacing(2),
            },
            label: {
                color: theme.relode.colors.label.main,
            },
        }),
    { name: "JobManagerContactDetails" }
);

interface JobManagerContactDetailsProps {
    title?: string;
    jobManagerContact?: JobManagerContact;
    contactActionVariant: "button" | "link";
    className?: string;
    jobId?: number;
    jobName?: string;
    leadId?: number;
    leadName?: string;
}

export const JobManagerContactDetails = (
    props: JobManagerContactDetailsProps
) => {
    const classes = useStyles({});
    const {
        title,
        jobManagerContact,
        contactActionVariant,
        className,
        jobId,
        jobName,
        leadId,
        leadName,
    } = props;

    const rootClasses = classnames(className, classes.root);

    if (!jobManagerContact) return null;
    return (
        <LayoutContainer>
            <div className={rootClasses}>
                {title ? (
                    <Text variant="h2">
                        {title}
                    </Text>
                ) : null}
                <div className={classes.detailsContainer}>
                    <Avatar
                        src={jobManagerContact.profileImageUrl}
                        alt={jobManagerContact.fullName}
                        initials={jobManagerContact.initials}
                        className={classes.avatar}
                    />
                    <div>
                        <Text dataCy="jobManagerName" variant="h3">
                            {jobManagerContact.fullName}
                        </Text>
                        <Text variant="caption" className={classes.label}>
                            Job Manager
                        </Text>
                    </div>
                </div>
                <div className={classes.contactLink}>
                    {jobManagerContact.phoneNumber && (
                        <PhoneLink
                            phone={jobManagerContact.phoneNumber}
                            variant="textAndIcon"
                            data-cy="jobManager-phone-link"
                        />
                    )}
                </div>

                {contactActionVariant === "button" ? (
                    <div className={classes.contactLink}>
                        <MessageComposerStandardButton
                            title="Send message"
                            jobName={jobName}
                            jobId={jobId}
                            leadName={leadName}
                            leadId={leadId}
                            initialRecipients={[
                                {
                                    userId: jobManagerContact.userId,
                                    displayName: jobManagerContact.fullName,
                                    roleName: jobManagerContact.roleName,
                                },
                            ]}
                        />
                    </div>
                ) : contactActionVariant === "link" ? (
                    <div className={classes.contactLink}>
                        {jobManagerContact.email && (
                            <EmailLink
                                email={jobManagerContact.email}
                                variant="textAndIcon"
                            />
                        )}
                    </div>
                ) : null}
            </div>
        </LayoutContainer>
    );
};
